import React, { useEffect, useRef, useState, type JSX } from "react";
import { CardContainer } from "../../../components/Card/CardContainer";
import { Col, Dropdown, Row, Tabs, Tooltip } from "antd";
import Input from "../../../components/Input/Input";
import { DownOutlined, SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import Select from "../../../components/Select/Select";
import { zoneDatasource } from "../../../datasource/ZoneDatasource";
import {
  getMapStatusWord,
  MAP_STATUS,
  MAP_STATUS_CHECK,
  MAP_STATUS_CRM,
  MAP_STATUS_DEALER,
} from "../../../definitions/approvePoint";
import { RangePicker } from "../../../components/DatePicker/DatePicker";
import color from "../../../resource/color";
import styled from "styled-components";
import StatusBadge from "../../../components/Badge/StatusBadge";
import TablePagination from "../../../components/Table/TablePagination";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dateFormatter, numberFormatter } from "../../../utility/Formatter";
import { PointApproveEntity } from "../../../entities/PointApproveEntity";
import { getPointApprove } from "../../../datasource/OrderPointDatasource";
import { EstimatePointFormatter } from "../../../utility/EstimatePointFormatter";
import { truncateText } from "../../../utility/truncateText";
import icon from "../../../resource/icon";
import SelectCheckbox from "../../../components/Select/SelectCheckbox";
import Text from "../../../components/Text/Text";
import useSorting from "../../../hook/useSorting";
import Button from "../../../components/Button/Button";
import { BaseText, DescText } from "../../../components/Text/Label";
import ModalReportApprovePoint from "../../../components/Modal/ModalReportApprovePoint";
import ModalError from "../../../components/Modal/ModalError";
import dayjs from "dayjs";

export const CircleDot = styled.div`
  width: 4px;
  height: 4px;
  background-color: ${color.placeholder};
  border-radius: 50%;
  margin: 0 5px;
`;

function IndexApprovePoint() {
  const company = JSON.parse(localStorage.getItem("company")!);
  const [searchParams, setSearchParams] = useSearchParams();
  const [zone, setZone] = useState<any>([]);
  const [showModal, setShowModal] = useState<{ key: number; open: boolean }>();
  const [modalErr, setModalErr] = useState(false);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState<{ count: number; data: PointApproveEntity[] }>({
    count: 0,
    data: [],
  });
  const [loading, setLoading] = useState(false);
  const { sortField, sortDirection, handleSortChange } = useSorting();
  const savedParams = JSON.parse(localStorage.getItem("approvePointListParams") || "{}");
  const [page, setPage] = useState<number>(parseInt(searchParams.get("page") || "1"));
  const [searchText, setSearchText] = useState<any>(savedParams.searchText || undefined);
  const [searchZone, setSearchZone] = useState<any>(savedParams.searchZone || undefined);
  const [selectStatus, setSelectStatus] = useState<any[]>(savedParams.selectStatus || []);
  const [statusCheck, setStatusCheck] = useState<any>(savedParams.statusCheck || []);
  const [activeTabKey, setActiveTabKey] = useState<string>(
    savedParams.activeTabKey || localStorage.getItem("crmTabs") || "DEALER",
  );
  const [dateFilter, setDateFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs] | undefined>(
    savedParams.startDate && savedParams.endDate
      ? [dayjs(savedParams.startDate), dayjs(savedParams.endDate)]
      : undefined,
  );
  const isDealer = activeTabKey === "DEALER";

  const saveParams = () => {
    const params = {
      page,
      searchText,
      searchZone,
      selectStatus,
      statusCheck,
      activeTabKey,
      startDate: dateFilter?.[0]?.format("YYYY-MM-DD") || "",
      endDate: dateFilter?.[1]?.format("YYYY-MM-DD") || "",
    };

    localStorage.setItem("approvePointListParams", JSON.stringify(params));

    setSearchParams({
      page: `${params.page}`,
      searchText: params.searchText,
      searchZone: params.searchZone,
      selectStatus: JSON.stringify(params.selectStatus),
      statusCheck: params.statusCheck,
      activeTabKey: params.activeTabKey,
      startDate: params.startDate,
      endDate: params.endDate,
    });
  };

  const getDataPointApprove = async () => {
    try {
      setLoading(true);
      setData({ count: 0, data: [] });
      const res = await getPointApprove({
        page,
        take: pageSize,
        userType: activeTabKey,
        company: company.companyCode,
        search: searchText || undefined,
        zone: searchZone || undefined,
        status: selectStatus.flatMap((el) => el.value),
        isChecked: statusCheck,
        startDate: dateFilter?.[0]?.format("YYYY-MM-DD 00:00:00"),
        endDate: dateFilter?.[1]?.format("YYYY-MM-DD 23:59:59"),
        sortBy: sortDirection ? sortField : undefined,
        sortDirection,
      });
      setData(res);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(getDataPointApprove, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    saveParams();
    getDataPointApprove();
  }, [
    page,
    searchText,
    searchZone,
    selectStatus,
    statusCheck,
    activeTabKey,
    dateFilter,
    sortField,
    sortDirection,
  ]);

  const tabsData: { key: string; label: React.ReactNode; children?: JSX.Element | undefined }[] = [
    {
      label: (
        <Text fontSize={14} fontWeight={700}>
          ให้สำหรับ Dealer เท่านั้น
        </Text>
      ),
      key: "DEALER",
    },
    {
      label: (
        <Text fontSize={14} fontWeight={700}>
          ให้สำหรับ Sub-Dealer เท่านั้น
        </Text>
      ),
      key: "SUB_DEALER",
    },
  ];
  useEffect(() => {
    const getZone = async () => {
      await zoneDatasource.getAllZoneByCompany(company?.companyCode).then((res) => {
        setZone(res);
      });
    };
    getZone();
  }, []);

  const columns: any[] = [
    {
      title: "เลขที่ส่งเอกสาร",
      dataIndex: "orderPointCode",
      key: "orderPointCode",
      render: (value: any, row: any) => ({
        children: (
          <>
            <BaseText>{value || "-"}</BaseText>
            <DescText>{dateFormatter(row.updateAt, true)}</DescText>
          </>
        ),
      }),
    },
    ...(isDealer
      ? [
          {
            title: "เลขที่ใบสั่งขาย (SO)",
            dataIndex: "soNo",
            key: "soNo",
            render: (value: any) => <BaseText>{value || "-"}</BaseText>,
          },
          {
            title: "เลขที่ใบส่งของ (PDO)",
            dataIndex: "pdoNo",
            key: "pdoNo",
            render: (value: any) => <BaseText>{value || "-"}</BaseText>,
          },
        ]
      : [
          {
            title: "วันที่ขน",
            dataIndex: "dateDelivery",
            key: "dateDelivery",
            render: (value: any, row: any) => ({
              children: <BaseText>{dateFormatter(row.dateDelivery, false)}</BaseText>,
            }),
          },
          {
            title: () => (
              <SortFieldCustom
                title='ร้าน Sub-Dealer'
                sortDirection={sortField === "subDealerName" ? sortDirection : undefined}
                onSortChange={() => handleSortChange("subDealerName")}
              />
            ),
            dataIndex: "subDealerName",
            key: "subDealerName",
            render: (value: any, row: any) => (
              <>
                <BaseText>{value || "-"}</BaseText>
                <DescText style={{ display: "flex", alignItems: "center" }}>
                  {row.subDealerNo} {row.subDealerNo || row.subDealerZone ? <CircleDot /> : null}
                  {row.subDealerZone}
                </DescText>
              </>
            ),
          },
        ]),
    {
      title: () => (
        <SortFieldCustom
          title='ร้าน Dealer'
          sortDirection={sortField === "dealerName" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("dealerName")}
        />
      ),
      dataIndex: "dealerName",
      key: "dealerName",
      render: (value: any, row: any) => (
        <>
          <BaseText>{value}</BaseText>
          <DescText style={{ display: "flex", alignItems: "center" }}>
            {row.dealerNo} {row.dealerNo || row.dealerZone ? <CircleDot /> : null}
            {row.dealerZone}
          </DescText>
        </>
      ),
    },
    {
      title: () => (
        <SortFieldCustom
          title='จำนวนสินค้า'
          sortDirection={sortField === "count" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("count")}
        />
      ),
      dataIndex: "count",
      key: "count",
      render: (value: any) => (
        <>
          <BaseText>{numberFormatter(value, 0)}</BaseText>
          <DescText>รายการ</DescText>
        </>
      ),
    },
    {
      title: () => (
        <SortFieldCustom
          title='แต้มที่ได้รับ'
          sortDirection={sortField === "sumPoint" ? sortDirection : undefined}
          onSortChange={() => handleSortChange("sumPoint")}
        />
      ),
      dataIndex: "sumPoint",
      key: "sumPoint",
      render: (value: any, row: any) => (
        <>
          <BaseText>
            <EstimatePointFormatter historyOrderPoint={row.historyOrderPoint} page='main' />
            &nbsp;{numberFormatter(value, 2)}
          </BaseText>
          <DescText>แต้ม</DescText>
        </>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (value: any, row: any) => {
        const lastHistory = Array.isArray(row.historyOrderPoint)
          ? row.historyOrderPoint[0] || {}
          : {};
        const { updateAt: lastUpdateAt, updateBy: lastUpdateBy = "", userType } = lastHistory;

        const getCancelReason = () => {
          switch (userType) {
            case "System":
              return "ระบบ";
            case "ADMIN":
              return lastUpdateBy;
            case "SUB_DEALER":
              return "ร้าน Sub-Dealer";
            default:
              return "";
          }
        };

        const cancelReason = getCancelReason();
        return {
          children: (
            <>
              <StatusBadge
                label={getMapStatusWord(isDealer ? "DEALER" : "SUB_DEALER")[value] || ""}
                bgColor={MAP_STATUS_CRM[value]}
              />
              {value === "CANCEL" && (
                <Tooltip title={cancelReason}>
                  <DescText>{truncateText(cancelReason, 20)}</DescText>
                </Tooltip>
              )}
              <DescText>{dateFormatter(lastUpdateAt ? lastUpdateAt : row.updateAt, true)}</DescText>
            </>
          ),
        };
      },
    },
    ...(isDealer
      ? []
      : [
          {
            title: "ตรวจสอบ",
            dataIndex: "isChecked",
            key: "isChecked",
            render: (value: any, row: any) => {
              const checkedActivity = row.historyOrderPoint.find(
                (item) => item.activity === "ตรวจสอบแล้ว",
              );
              const updateBy = checkedActivity ? checkedActivity.updateBy : "";
              return {
                children: (
                  <>
                    {row.isChecked ? (
                      <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <BaseText>ตรวจแล้ว</BaseText>
                        <img src={icon.checked} style={{ width: 18, height: 18, marginLeft: 4 }} />
                      </div>
                    ) : (
                      <BaseText>-</BaseText>
                    )}
                    <DescText>{truncateText(updateBy, 20)}</DescText>
                  </>
                ),
              };
            },
          },
        ]),
    {
      title: "",
      dataIndex: "action",
      key: "action",
      render: (value: any, row: any) => ({
        children: (
          <div className='btn btn-icon btn-light btn-hover-primary btn-sm'>
            <UnorderedListOutlined
              style={{ color: color["primary"] }}
              onClick={() => navigate("/crm/approvePointList/" + row.orderPointId)}
            />
          </div>
        ),
      }),
    },
  ];

  const SectionSearch = () => {
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        <Col span={16}>
          <Row gutter={[8, 8]}>
            <Col span={isDealer ? 8 : 6}>
              <Input
                placeholder={
                  isDealer
                    ? "เลขที่คำสั่งซื้อ / SO / PDO / รหัสร้าน / ชื่อร้าน"
                    : "เลขที่เอกสาร / รหัส / ชื่อร้าน"
                }
                suffix={<SearchOutlined />}
                defaultValue={searchText}
                onPressEnter={(e) => {
                  const value = (e.target as HTMLInputElement).value;
                  setSearchText(value);
                  setPage(1);
                }}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value;
                  if (!value) {
                    setSearchText("");
                    setPage(1);
                  }
                }}
              />
            </Col>
            <Col span={5}>
              <SelectCheckbox
                selectedOptions={selectStatus}
                setSelectedOptions={(e) => {
                  setSelectStatus(e);
                  setPage(1);
                }}
                placeholder='สถานะทั้งหมด'
                listOption={isDealer ? MAP_STATUS_DEALER : MAP_STATUS}
                maxTagCount={2}
              />
            </Col>
            {activeTabKey === "SUB_DEALER" && (
              <Col span={5}>
                <Select
                  allowClear
                  placeholder='การตรวจสอบทั้งหมด'
                  data={MAP_STATUS_CHECK}
                  value={statusCheck}
                  style={{ width: "100%" }}
                  onChange={(e) => {
                    setStatusCheck(e);
                    setPage(1);
                  }}
                />
              </Col>
            )}
            <Col span={4}>
              <Select
                value={searchZone}
                allowClear
                placeholder='เขตทั้งหมด'
                data={zone.map((z) => ({
                  label: z.zoneName,
                  value: z.zoneName,
                  key: z.zoneId,
                }))}
                style={{ width: "100%", fontFamily: "Sarabun" }}
                onChange={(e) => {
                  setSearchZone(e);
                  setPage(1);
                }}
              />
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <Row gutter={[8, 0]} align='middle'>
            <Col flex='1'>
              <RangePicker
                allowEmpty={[true, true]}
                enablePast
                value={dateFilter}
                onChange={(dates, dateString) => {
                  if (!dates || (dates[0] && dates[1])) {
                    setPage(1);

                    setDateFilter(dates);
                  }
                }}
                style={{ width: "100%" }}
              />
            </Col>
            <Col>
              <Dropdown
                menu={{
                  items: [
                    {
                      label: <span style={{ fontSize: 16 }}>รายงานอนุมัติแต้ม</span>,
                      key: "1",
                      onClick: () => setShowModal({ key: 1, open: true }),
                    },
                    {
                      label: <span style={{ fontSize: 16 }}>รายงานอนุมัติแต้มแบบ Pivot</span>,
                      key: "2",
                      onClick: () => setShowModal({ key: 2, open: true }),
                    },
                  ],
                }}
              >
                <Button
                  title={
                    <>
                      ดาวน์โหลด&nbsp;&nbsp;
                      <DownOutlined style={{ fontSize: 10, marginBottom: 4 }} />
                    </>
                  }
                  icon={<img src={icon.downloadFileIcon} style={{ width: 14, height: 18 }} />}
                  typeButton='primary-light'
                />
              </Dropdown>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };
  return (
    <CardContainer>
      <Row align='middle' gutter={16} justify='start' style={{ marginBottom: "28px" }}>
        <Col className='gutter-row' span={4}>
          <div>
            <span
              className='card-label font-weight-bolder text-dark'
              style={{ fontSize: 20, fontWeight: "700" }}
            >
              รายการอนุมัติแต้ม
            </span>
          </div>
        </Col>
      </Row>
      <Tabs
        items={tabsData}
        activeKey={activeTabKey}
        onChange={(e) => {
          if (e !== activeTabKey) setActiveTabKey(e);
          setSearchText(undefined);
          setSearchZone(undefined);
          setStatusCheck(undefined);
          setSelectStatus([]);
          setDateFilter(undefined);
          setPage(1);

          setPageSize(10);
        }}
      />
      <SectionSearch />

      <TablePagination
        columns={columns}
        data={(data.data || []).map((item: any, index: number) => {
          return {
            ...item,
            id: index + 1,
          };
        })}
        isLoading={loading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          total: data?.count || 0,
          pageSize: pageSize,
        }}
        locale={{
          emptyText: (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 350,
              }}
            >
              <img
                src={icon.emptyHistoryPoint}
                style={{
                  width: 80,
                  height: 90,
                }}
                alt='empty_table'
              />
              <Text fontFamily='Sarabun' fontSize={14} fontWeight={400} color='Text3'>
                ไม่มีรายการอนุมัติแต้ม
              </Text>
            </div>
          ),
        }}
        isQuickJumper
        onPageChange={(pageNum) => setPage(pageNum)}
      />

      <ModalReportApprovePoint
        open={showModal?.open || false}
        onClose={() => setShowModal({ key: 0, open: false })}
        userType={activeTabKey}
        onCallErr={(val) => {
          if (val === "err") {
            setModalErr(true);
          }
        }}
        reportType={showModal?.key || 0}
      />
      <ModalError
        visible={modalErr}
        icon={icon.network_err}
        iconH={150}
        iconW={95}
        onSubmit={() => setModalErr(false)}
        header='ไม่สามารถดาวน์โหลดไฟล์ได้'
        label='เนื่องจากข้อมูลของคุณมีจำนวนมาก (ไฟล์ Excel รองรับข้อมูลได้สูงสุด 80,000 แถว) กรุณาลอง ลดช่วงข้อมูลที่เลือก แล้วดาวน์โหลดใหม่อีกครั้ง'
        confirmText='เข้าใจแล้ว'
      />
    </CardContainer>
  );
}

export default IndexApprovePoint;
